import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { NavigateFunction } from 'react-router-dom'
import { RefreshActions } from '../state/refresh'
import { useRequests } from '../requests/useRequests'
import { Brand } from '../types/brand'
import { Product } from '../types/product'

export const useBrandDetails = (brandId: number, navigate: NavigateFunction) => {
  const dispatch = useDispatch()
  const requests = useRequests()

  const [brand, setBrand] = useState<Brand | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [isBrandVisited, setIsBrandVisited] = useState<boolean>(false)
  const itemsPerPage = 9

  const showToast = (message: string) => {
    dispatch(
      RefreshActions.setToastMessage({
        message
      })
    )
  }

  useEffect(() => {
    const trackBrandVisit = async () => {
      try {
        await requests.trackBrandVisit(brandId)
      } catch (error) {
        console.error('Błąd podczas śledzenia odwiedzin marki:', error)
      }
    }

    if (!loading && !isBrandVisited) {
      setIsBrandVisited(true)
      trackBrandVisit()
    }
  }, [loading, isBrandVisited])

  useEffect(() => {
    setLoading(true)

    const fetchBrandData = async () => {
      try {
        const brandResponse = await requests.getBrand(brandId)
        setBrand(brandResponse.data)
        loadProducts()
      } catch (error) {
        console.error('Błąd podczas pobierania danych marki:', error)
        showToast('Nie udało się pobrać danych marki')
        setLoading(false)
        navigate('/brands')
      }
    }

    fetchBrandData()
  }, [brandId, navigate, dispatch])

  const loadProducts = () => {
    requests.getProducts({
      page: currentPage,
      limit: itemsPerPage,
      brandId,
      searchQuery: searchQuery || undefined,
      category: selectedCategory || undefined
    })
      .then(response => {
        setProducts(response.data.data)
        setTotalPages(response.data.meta.pages)
        setLoading(false)
      })
      .catch(error => {
        console.error('Błąd podczas pobierania produktów:', error)
        showToast('Nie udało się pobrać produktów')
        setLoading(false)
      })
  }

  useEffect(() => {
    if (brand != null) {
      loadProducts()
    }
  }, [currentPage, searchQuery, selectedCategory])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setCurrentPage(1)
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return {
    brand,
    products,
    loading,
    searchQuery,
    selectedCategory,
    currentPage,
    totalPages,
    loadProducts,
    handleSearch,
    handleCategoryChange,
    handlePageChange,
    showToast
  }
}
